import { Calendar, MapPin, Search, Users, X, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';

/**
 * Professional Job Selector Modal
 * Based on conversation-mockup.html design
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal visibility
 * @param {Function} props.onClose - Close handler
 * @param {Array} props.jobs - Array of job descriptions
 * @param {Function} props.onSelect - Job selection handler
 * @param {boolean} props.isLoading - Loading state
 */
export function JobSelectorModal({
  isOpen,
  onClose,
  jobs = [],
  onSelect,
  isLoading = false
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredJobs, setFilteredJobs] = useState(jobs);

  // Filter jobs when search term or jobs change
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredJobs(jobs);
    } else {
      const q = searchTerm.toLowerCase();
      const filtered = jobs.filter(job =>
        job.title?.toLowerCase().includes(q) ||
        job.company?.toLowerCase().includes(q) ||
        job.location?.toLowerCase().includes(q) ||
        job.filename?.toLowerCase().includes(q) ||
        job.id?.toLowerCase().includes(q)
      );
      setFilteredJobs(filtered);
    }
  }, [searchTerm, jobs]);

  const handleSelect = (job) => {
    onSelect(job);
    onClose();
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full',
      archived: 'px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full',
      in_progress: 'px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full'
    };
    return badges[status] || badges.active;
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Active',
      archived: 'Archived',
      in_progress: 'In Progress'
    };
    return labels[status] || 'Active';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Previous Job Descriptions</h2>
            <p className="text-sm text-gray-500 mt-1">Select a job description to search for candidates</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="relative">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search job descriptions by title, company, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f7911d] focus:border-transparent text-sm"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="mt-2 text-xs text-gray-600">
            <span>{filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''} found</span>
          </div>
        </div>

        {/* Job List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#f7911d]"></div>
              <p className="mt-4 text-gray-500">Loading job descriptions...</p>
            </div>
          ) : filteredJobs.length > 0 ? (
            filteredJobs.map((job) => (
              <div
                key={job.id}
                className="px-6 py-4 border-b border-gray-200 hover:bg-gray-50 transition cursor-pointer group"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900 text-base truncate">
                        {job.title}
                      </h3>
                      <span className={getStatusBadge(job.status)}>
                        {getStatusLabel(job.status)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {job.company?.trim() ? job.company : 'No company'}
                    </p>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      {job.location && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          <span>{job.location}</span>
                        </div>
                      )}
                      {job.created_at && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>Created: {new Date(job.created_at).toLocaleDateString()}</span>
                        </div>
                      )}
                      {job.candidate_count !== undefined && (
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          <span>{job.candidate_count} candidates found</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleSelect(job)}
                    className="px-4 py-2 bg-[#f7911d] text-white rounded-lg hover:bg-[#e67e0a] transition text-sm font-medium opacity-0 group-hover:opacity-100"
                  >
                    Select
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="py-12 text-center text-gray-500">
              <p className="text-lg mb-2">No job descriptions found</p>
              <p className="text-sm">
                {searchTerm
                  ? 'Try adjusting your search terms'
                  : 'Upload a new job description to get started'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default JobSelectorModal;























